// Sources de données — /connectors (docs/data-sources-v2-spec.md §4–5,
// docs/ticket-journey.md). What the account's systems (ERP, shops, WMS, TMS,
// tracking APIs, web portals) bring to each ticket, and what still needs a
// person. Header → « À vérifier » (failing sources, ended comparisons, what
// the journey says is missing) → tiles (journey readiness, sources, orders
// found over 7 days, failures) → views (?tab=):
// « Parcours d'un ticket » (TicketJourney), « Sources » (SourcesByCategory,
// with each source's health) and « Réglages de l'enrichissement »
// (TicketWorkflow). Sub-views: a new source (?new=choose|api|web|catalog
// [&category=]) and a source's page (?source=<key>[&sourceTab=]).
// Deep links kept (consumed once, then rewritten): ?view=workflow (backend
// setup health, /ai-automation?tab=workflow), ?view=choose[&category=]
// (journey item, onboarding), ?step=<journey step> (onboarding, copilot).
// API: GET /api/integrations/connectors, /catalog, GET /api/setup/journey.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
    ChevronRight, CircleAlert, Database, PackageSearch, Plus, RefreshCw, Route, SlidersHorizontal, TriangleAlert,
} from 'lucide-react';
import {
    Badge, Banner, Card, Kpi, PageHeader, Tabs, Toast,
} from '../design/ui';
import TicketWorkflow from './TicketWorkflow';
import TicketJourney, { itemDetail, itemTitle, stepTitle } from './dataSources/TicketJourney';
import SourcesByCategory, { CATEGORIES, categoryTitle } from './dataSources/SourcesByCategory';
import {
    API, ApiWizard, authHeaders, CatalogInstall, ChoosePath, WebWizard,
} from './DataSourcesCreate';
import SourcePage from './DataSourcesSource';
import {
    connectorsByKey, formatCount, formatPercent, formatShortDay, formatStamp, reasonText, sourcesAttention, sourcesSummary,
} from './dataToolsModel';
import './DataSources.css';

const TABS = ['journey', 'sources', 'workflow'];
const NEW_PATHS = ['choose', 'api', 'web', 'catalog'];

const canEdit = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || '{}').role !== 'employee';
    } catch (e) {
        return true;
    }
};

const DataSources = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const editable = canEdit();

    const [listing, setListing] = useState(undefined); // undefined = loading, null = failed
    const [catalog, setCatalog] = useState([]);
    const [journey, setJourney] = useState(undefined); // undefined = loading, null = failed
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [stepRequest, setStepRequest] = useState(null);
    // ?step=<journey step> opens that step (onboarding, copilot, « Configurer » links).
    const [initialStep] = useState(() => searchParams.get('step'));

    // URL state: the view of the page, a creation in progress, a source.
    const legacyView = searchParams.get('view');
    const tabParam = searchParams.get('tab');
    const tab = TABS.includes(tabParam) ? tabParam : (legacyView === 'workflow' ? 'workflow' : 'journey');
    const newPath = NEW_PATHS.includes(searchParams.get('new')) ? searchParams.get('new') : (legacyView === 'choose' ? 'choose' : null);
    const categoryParam = CATEGORIES.some((c) => c.key === searchParams.get('category')) ? searchParams.get('category') : null;
    const sourceKey = searchParams.get('source');
    const sourceTab = searchParams.get('sourceTab');

    const updateParams = useCallback((updates, { replace = false } = {}) => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            // The legacy deep-link params never outlive the first change.
            next.delete('view');
            next.delete('step');
            Object.entries(updates).forEach(([key, value]) => {
                if (value === null || value === undefined || value === '') next.delete(key);
                else next.set(key, String(value));
            });
            return next;
        }, { replace });
    }, [setSearchParams]);

    // The historical deep links are consumed once and rewritten into the
    // page's own state, so that going back to the list does not reopen them.
    useEffect(() => {
        const view = searchParams.get('view');
        if (view || searchParams.get('step')) {
            updateParams({
                view: null,
                step: null,
                tab: view === 'workflow' ? 'workflow' : (searchParams.get('tab') || null),
                new: view === 'choose' ? 'choose' : (searchParams.get('new') || null),
            }, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showToast = (tone, text) => setToast({ tone, text });
    useEffect(() => {
        if (!toast || toast.tone === 'danger') return undefined;
        const timer = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(timer);
    }, [toast]);

    const loadJourney = useCallback(async () => {
        try {
            const res = await axios.get('/api/setup/journey', authHeaders());
            setJourney(res.data);
        } catch (e) {
            setJourney(null);
        }
    }, []);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [list, cat] = await Promise.all([axios.get(API, authHeaders()), axios.get(`${API}/catalog`, authHeaders())]);
            setListing(list.data);
            setCatalog(cat.data.entries || []);
        } catch (e) {
            setListing((previous) => previous || null);
            setError(t('dataSources.loadError', 'Impossible de charger les sources de données.'));
        }
        await loadJourney();
    }, [t, loadJourney]);

    useEffect(() => { load(); }, [load]);

    const connectors = useMemo(() => listing?.connectors || [], [listing]);
    const byKey = useMemo(() => connectorsByKey(connectors), [connectors]);
    const summary = useMemo(() => sourcesSummary(journey, connectors), [journey, connectors]);
    const attention = useMemo(() => (journey || listing ? sourcesAttention(journey, connectors) : []), [journey, listing, connectors]);

    const goHome = async (message, nextTab = null) => {
        updateParams({ new: null, category: null, source: null, sourceTab: null, tab: nextTab });
        await load();
        if (message) showToast('success', message);
    };
    const setTab = (next) => updateParams({ tab: next === 'journey' ? null : next }, { replace: true });
    const openSource = (key, nextTab = null) => updateParams({ source: key, sourceTab: nextTab, new: null, category: null });
    const startNew = (category = null) => updateParams({ new: 'choose', category, source: null, sourceTab: null });
    const showStep = (key) => {
        setStepRequest({ key, n: Date.now() });
        setTab('journey');
    };

    const created = (key) => goHome(t('dataSources.created', 'Source « {{key}} » enregistrée et active.', { key }), 'sources');
    // « Convertir » (POST /migrate/*): an HTTP source keeps its key and keeps
    // enriching; EffiTrace gets a new source compared with the current one.
    const convertedText = (key, kind) => (kind === 'effitrace'
        ? t('settingsPolishUi.dataSources.convertedEffitrace', 'Source « {{key}} » créée : elle est comparée au mécanisme actuel avant de le remplacer.', { key })
        : t('settingsPolishUi.dataSources.converted', 'Source « {{key}} » convertie : vérifiez-la avec une vraie commande.', { key }));

    // ---- zones ----------------------------------------------------------------

    const loading = journey === undefined;

    const subtitle = () => {
        if (loading) return t('dataToolsUi.page.subtitleLoading', 'Les systèmes qui complètent chaque ticket : commande, colis, suivi.');
        if (!journey) return t('dataSources.introJourney', 'Vos systèmes — ERP, boutiques, WMS, TMS, API de suivi — complètent chaque ticket avec la commande, les colis et le suivi. Le parcours ci-dessous montre à quelle étape chacun intervient et ce qui manque encore.');
        if (!summary.total) return t('dataToolsUi.page.subtitleEmpty', 'Aucune source à vous encore : ajoutez votre WMS, votre ERP ou vos boutiques pour que l’agent réponde avec la vraie commande.');
        return [
            t('dataToolsUi.page.subtitleSources', '{{count}} sources', { count: summary.total }),
            t('dataToolsUi.page.subtitleArrival', '{{count}} lues à l’arrivée', { count: summary.atArrival }),
            summary.failing ? t('dataToolsUi.page.subtitleFailing', '{{count}} en échec', { count: summary.failing }) : null,
        ].filter(Boolean).join(' · ');
    };

    const attentionRow = (item) => {
        if (item.kind === 'failing') {
            return {
                badge: t('dataToolsUi.attention.blocking', 'Bloquant'),
                title: t('dataToolsUi.attention.failing', 'La source « {{title}} » ne lit plus rien', { title: item.title }),
                detail: [
                    reasonText(item.reason, t),
                    item.since ? t('dataToolsUi.attention.since', 'depuis le {{date}}', { date: formatStamp(item.since) }) : null,
                    item.consecutive ? t('dataToolsUi.sources.consecutive', '{{count}} échecs de suite', { count: item.consecutive }) : null,
                ].filter(Boolean).join(' · '),
                action: <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openSource(item.sourceKey, 'history')}>{t('dataToolsUi.see', 'Voir')} <ChevronRight aria-hidden="true" /></button>,
            };
        }
        if (item.kind === 'verified') {
            return {
                badge: t('dataToolsUi.attention.decide', 'À décider'),
                title: t('dataToolsUi.attention.verified', 'Comparaison terminée pour « {{title}} »', { title: item.title }),
                detail: t('dataToolsUi.attention.verifiedDetail', 'Fin prévue le {{date}} : comparez les résultats, puis remplacez « {{legacy}} » ou gardez-le.', { date: formatShortDay(item.endedAt), legacy: item.shadowOf }),
                action: <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openSource(item.sourceKey, 'verification')}>{t('dataToolsUi.see', 'Voir')} <ChevronRight aria-hidden="true" /></button>,
            };
        }
        const index = (journey?.steps || []).findIndex((s) => s.key === item.step);
        const detail = itemDetail(item.item, t);
        return {
            badge: item.tone === 'danger' ? t('dataToolsUi.attention.blocking', 'Bloquant') : t('dataToolsUi.attention.watch', 'À surveiller'),
            title: itemTitle(item.item.key, t),
            detail: [`${index + 1}. ${stepTitle(item.step, t)}`, detail].filter(Boolean).join(' · '),
            action: item.item.route && !String(item.item.route).startsWith('/connectors')
                ? <Link className="u-btn u-btn--secondary u-btn--sm" to={item.item.route}>{t('journey.configure', 'Configurer')} <ChevronRight aria-hidden="true" /></Link>
                : <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => showStep(item.step)}>{t('dataToolsUi.see', 'Voir')} <ChevronRight aria-hidden="true" /></button>,
        };
    };

    const renderAttention = () => {
        if (!attention.length) return null;
        return (
            <Card icon={TriangleAlert} iconTone="warning" title={t('dataToolsUi.attention.title', 'À vérifier')}
                badge={<Badge tone="warning">{attention.length}</Badge>} flush className="ds-attention">
                <ul className="u-list">
                    {attention.map((item) => {
                        const row = attentionRow(item);
                        return (
                            <li className="u-row" key={item.key}>
                                <Badge tone={item.tone} dot>{row.badge}</Badge>
                                <div className="u-row__main">
                                    <div className="u-row__title">{row.title}</div>
                                    <div className="u-row__meta" title={row.detail}>{row.detail}</div>
                                </div>
                                <div className="u-row__side">{row.action}</div>
                            </li>
                        );
                    })}
                </ul>
            </Card>
        );
    };

    const renderKpis = () => {
        if (loading) {
            return (
                <div className="u-kpi-row ds-kpis" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton ds-kpi-skeleton" />)}
                </div>
            );
        }
        if (!journey) return null;
        const next = (journey.steps || []).find((s) => s.key === journey.next_step);
        const nextIndex = (journey.steps || []).indexOf(next);
        const tiles = [
            <Kpi
                key="journey"
                icon={Route}
                tone={journey.ready === journey.total ? 'success' : undefined}
                label={t('dataToolsUi.kpi.journey', 'Parcours prêt')}
                value={`${journey.ready}/${journey.total}`}
                context={next
                    ? t('dataToolsUi.kpi.journeyNext', 'à régler : {{n}}. {{step}}', { n: nextIndex + 1, step: stepTitle(next.key, t) })
                    : t('dataToolsUi.kpi.journeyDone', 'toutes les étapes sont prêtes')}
                meter={{ value: journey.ready, max: journey.total, tone: 'success', label: t('journey.progressLabel', 'Étapes prêtes') }}
                onClick={() => (next ? showStep(next.key) : setTab('journey'))}
                active={tab === 'journey'}
            />,
            <Kpi
                key="sources"
                icon={Database}
                tone={summary.total ? undefined : 'neutral'}
                label={t('dataToolsUi.kpi.sources', 'Vos sources')}
                value={formatCount(summary.total)}
                context={summary.total
                    ? t('dataToolsUi.kpi.sourcesContext', '{{count}} lues à l’arrivée des tickets', { count: summary.atArrival })
                    : t('dataToolsUi.kpi.sourcesNone', 'suivi Ship24 inclus seulement')}
                onClick={() => setTab('sources')}
                active={tab === 'sources'}
            />,
        ];
        if (listing && summary.measured > 0) {
            tiles.push(summary.reads > 0 ? (
                <Kpi
                    key="found"
                    icon={PackageSearch}
                    tone="success"
                    label={t('dataToolsUi.kpi.found', 'Commandes trouvées')}
                    value={formatPercent(summary.foundRate)}
                    context={t('dataToolsUi.kpi.foundContext', '{{found}} sur {{reads}} lectures · 7 jours', { found: formatCount(summary.found), reads: formatCount(summary.reads) })}
                    meter={{ value: summary.found, max: summary.reads, tone: 'success', label: t('dataToolsUi.kpi.found', 'Commandes trouvées') }}
                    onClick={() => setTab('sources')}
                />
            ) : (
                <Kpi key="found" icon={PackageSearch} tone="neutral" label={t('dataToolsUi.kpi.reads', 'Lectures sur 7 jours')} value="0"
                    context={t('dataToolsUi.kpi.readsNone', 'aucun ticket n’a interrogé vos sources')} onClick={() => setTab('sources')} />
            ));
        }
        if (listing) {
            tiles.push(
                <Kpi
                    key="failing"
                    icon={CircleAlert}
                    tone={summary.failing ? 'danger' : 'success'}
                    label={t('dataToolsUi.kpi.failing', 'Sources en échec')}
                    value={formatCount(summary.failing)}
                    context={summary.measured
                        ? t('dataToolsUi.kpi.failingContext', '{{count}} lectures en échec · 7 jours', { count: summary.failed })
                        : t('dataToolsUi.kpi.failingNone', 'aucune source à surveiller')}
                    contextTone={summary.failing ? 'danger' : undefined}
                    onClick={() => setTab('sources')}
                />,
            );
        }
        return <div className={`u-kpi-row ds-kpis ds-kpis--${tiles.length}`}>{tiles}</div>;
    };

    const journeyUnavailable = (
        <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={loadJourney}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>
            {t('dataSources.journeyUnavailable', 'Le parcours n’a pas pu être chargé.')}
        </Banner>
    );

    const renderPanel = () => {
        if (tab === 'workflow') return <TicketWorkflow canEdit={editable} onFeedback={(feedback) => feedback && showToast(feedback.type === 'error' ? 'danger' : 'success', feedback.message || feedback.text || '')} />;
        if (loading) {
            return (
                <div className="ds-skeleton" aria-busy="true" aria-label={t('dataSources.loading', 'Chargement…')}>
                    {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
                </div>
            );
        }
        if (!journey) return journeyUnavailable;
        if (tab === 'sources') {
            return (
                <SourcesByCategory sources={journey.sources} connectors={connectors} canEdit={editable}
                    onAdd={(category) => startNew(category)}
                    onOpen={(key) => openSource(key)}
                    onUpgraded={(key, kind) => goHome(convertedText(key, kind), 'sources')}
                    onUpgradeError={(message) => showToast('danger', message)} />
            );
        }
        return <TicketJourney journey={journey} canEdit={editable} onChanged={loadJourney} initialStep={initialStep} stepRequest={stepRequest} />;
    };

    const backLink = (
        <Link to="/connectors" onClick={(e) => { e.preventDefault(); goHome(); }}>{t('dataSources.title', 'Sources de données')}</Link>
    );

    const toastNode = toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>{toast.text}</Toast>
    );

    // ---- a source's page --------------------------------------------------------
    if (sourceKey) {
        const connector = byKey[sourceKey];
        return (
            <div className="u-page ds-page">
                <PageHeader
                    breadcrumb={<>{backLink}<ChevronRight aria-hidden="true" className="ds-crumb-sep" /><span>{connector?.title || sourceKey}</span></>}
                    title={connector?.title || sourceKey}
                    subtitle={connector?.category ? categoryTitle(connector.category, t) : null}
                    actions={(
                        <button type="button" className="u-btn u-btn--secondary" onClick={() => goHome(null, 'sources')}>
                            {t('dataToolsUi.backToSources', 'Toutes les sources')}
                        </button>
                    )}
                />
                <div className="u-well">
                    <SourcePage t={t} keyName={sourceKey} connector={connector} canEdit={editable}
                        tab={sourceTab} onTab={(next) => updateParams({ sourceTab: next === 'steps' ? null : next }, { replace: true })}
                        onDeleted={() => goHome(t('dataSources.deleted', 'Source supprimée.'), 'sources')}
                        onChanged={(message) => { load(); if (message) showToast('success', message); }}
                        onConverted={(key, kind) => {
                            load();
                            if (key !== sourceKey) openSource(key, 'verification');
                            showToast('success', convertedText(key, kind));
                        }} />
                </div>
                {toastNode}
            </div>
        );
    }

    // ---- a new source -------------------------------------------------------------
    if (newPath) {
        const titles = {
            choose: t('dataToolsUi.create.title', 'Ajouter une source'),
            api: t('dataToolsUi.create.apiTitle', 'Nouvelle source par API'),
            web: t('dataToolsUi.create.webTitle', 'Nouvelle source par le site web'),
            catalog: t('dataSources.catalog.title', 'Systèmes connus'),
        };
        const back = () => updateParams({ new: newPath === 'choose' ? null : 'choose', category: newPath === 'choose' ? null : categoryParam });
        return (
            <div className="u-page ds-page">
                <PageHeader
                    breadcrumb={<>{backLink}<ChevronRight aria-hidden="true" className="ds-crumb-sep" /><span>{t('dataToolsUi.create.crumb', 'Nouvelle source')}</span></>}
                    title={titles[newPath]}
                    subtitle={categoryParam
                        ? t('dataSources.choose.forCategory', 'Nouvelle source : {{category}}', { category: categoryTitle(categoryParam, t) })
                        : t('dataToolsUi.create.subtitle', 'Unanimity lit la commande, les colis et le suivi ; rien n’est modifié dans votre système.')}
                    actions={(
                        <button type="button" className="u-btn u-btn--secondary" onClick={() => goHome()}>{t('common.cancel', 'Annuler')}</button>
                    )}
                />
                <div className="u-well">
                    {!editable && <Banner tone="warning">{t('dataToolsUi.readOnly', 'Seul un administrateur peut ajouter ou modifier une source.')}</Banner>}
                    {newPath === 'choose' && (
                        <ChoosePath t={t}
                            onApi={() => updateParams({ new: 'api' })}
                            onWeb={() => updateParams({ new: 'web' })}
                            onCatalog={() => updateParams({ new: 'catalog' })} />
                    )}
                    {newPath === 'api' && <ApiWizard t={t} category={categoryParam} onBack={back} onDone={created} />}
                    {newPath === 'web' && <WebWizard t={t} catalog={catalog} category={categoryParam} onBack={back} onDone={created} />}
                    {newPath === 'catalog' && <CatalogInstall t={t} entries={catalog} onBack={back} onDone={created} />}
                </div>
                {toastNode}
            </div>
        );
    }

    // ---- the page ---------------------------------------------------------------------
    return (
        <div className="u-page ds-page">
            <PageHeader
                title={t('dataSources.title', 'Sources de données')}
                subtitle={subtitle()}
                actions={(
                    <>
                        <button type="button" className="u-btn u-btn--secondary u-btn--icon" onClick={load}
                            title={t('dataSources.reload', 'Actualiser')} aria-label={t('dataSources.reload', 'Actualiser')}>
                            <RefreshCw aria-hidden="true" />
                        </button>
                        {editable && (
                            <button type="button" className="u-btn u-btn--primary" onClick={() => startNew()}>
                                <Plus aria-hidden="true" /> {t('dataSources.add', 'Ajouter une source')}
                            </button>
                        )}
                    </>
                )}
            />
            <div className="u-well">
                {error && (
                    <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={load}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>
                        {error}
                    </Banner>
                )}
                {renderAttention()}
                {renderKpis()}
                <Tabs
                    label={t('dataSources.title', 'Sources de données')}
                    active={tab}
                    onChange={setTab}
                    tabs={[
                        { key: 'journey', label: t('journey.title', 'Parcours d’un ticket'), icon: Route },
                        { key: 'sources', label: t('dataToolsUi.tabs.sources', 'Sources'), icon: Database },
                        { key: 'workflow', label: t('dataSources.workflowSettings', 'Réglages de l’enrichissement'), icon: SlidersHorizontal },
                    ]}
                />
                <div role="tabpanel">{renderPanel()}</div>
            </div>
            {toastNode}
        </div>
    );
};

export default DataSources;
